import moment from 'moment';
import { CurrencyConstants } from 'finhelper';
import {
  isActivePaymentRelationTypeKey,
  resolveActivePaymentExchangePlan,
  pickPreferredActivePaymentRelationType,
  resolveActivePaymentKindFromRelationType,
} from '#app/constants/activePaymentRelationTypes';
import type {
  ApiPaymentRow,
  ApiPaymentsByInstrument,
  ApiPaymentsByRelationType,
  ApiPaymentsData,
  CalendarPaymentItem,
} from '#app/hooks/queries/usePaymentsAllQuery/types';

function normalizePaymentsDate(dateKey: string): string {
  return moment(dateKey, ['DD.MM.YYYY', moment.ISO_8601], true).isValid()
    ? moment(dateKey, ['DD.MM.YYYY', moment.ISO_8601]).format('YYYY-MM-DD')
    : dateKey;
}

function isAccountPaymentRow(value: unknown): value is ApiPaymentRow {
  return Boolean(value && typeof value === 'object' && ('sum' in value || 'plan_sum' in value));
}

function resolveCompanyNameFromPayment(payment: ApiPaymentRow, tickerOrIsin: string): string {
  const itemName = payment.item?.name ?? payment.item?.shortname;
  if (itemName && String(itemName).trim()) return String(itemName).trim();

  return payment.company_name ?? payment.issuer_name ?? payment.name_text ?? tickerOrIsin;
}

function buildCalendarPaymentItem(params: {
  dateKey: string;
  normalizedDate: string;
  tickerOrIsin: string;
  relationType: string;
  accountId: string;
  payment: ApiPaymentRow;
}): CalendarPaymentItem {
  const { dateKey, normalizedDate, tickerOrIsin, relationType, accountId, payment } = params;
  const paymentKind = resolveActivePaymentKindFromRelationType(relationType);
  const accountIdNum = Number(payment.account_id ?? accountId);
  const tickerHubId = payment.ticker_hub_id != null ? Number(payment.ticker_hub_id) : undefined;
  const relationIdRaw = payment.relation?.id ?? payment.relation_id;
  const relationIdNum = relationIdRaw != null ? Number(relationIdRaw) : NaN;

  return {
    id: `${dateKey}-${tickerOrIsin}-${relationType}-${accountId}`,
    isin: payment.isin ?? tickerOrIsin,
    item: payment.item ?? null,
    name_text: payment.name_text ?? '',
    type_id: payment.type_id ?? '',
    ticker: payment.ticker ?? tickerOrIsin,
    sum: payment.sum ?? 0,
    plan_sum: payment.plan_sum ?? null,
    original_plan_sum: payment.original_plan_sum,
    original_sum: payment.original_sum,
    currency_id: payment.currency_id,
    currency: CurrencyConstants.getCurrencySignById(payment.currency_id ?? 1),
    date: payment.date ? moment(payment.date).format('YYYY-MM-DD') : normalizedDate,
    company_name: resolveCompanyNameFromPayment(payment, tickerOrIsin),
    payment_type: paymentKind ?? payment.payment_type ?? 'dividend',
    relation_type: relationType,
    from_exchange_plan: resolveActivePaymentExchangePlan({ relationType }),
    active_account_id: Number.isFinite(accountIdNum) ? accountIdNum : null,
    count: payment.count,
    lotsize: payment.lotsize,
    catalog_hub_id: Number.isFinite(tickerHubId) ? tickerHubId : undefined,
    relation_id: Number.isFinite(relationIdNum) ? relationIdNum : undefined,
    relation: payment.relation ?? null,
    is_confirmed: payment.is_confirmed,
    raw: {
      ...payment,
      relation_type: relationType,
      account_id: payment.account_id ?? accountId,
    },
  };
}

function flattenInstrumentByRelationType(params: {
  dateKey: string;
  normalizedDate: string;
  tickerOrIsin: string;
  byRelationType: ApiPaymentsByRelationType;
}): CalendarPaymentItem[] {
  const { dateKey, normalizedDate, tickerOrIsin, byRelationType } = params;
  const relationKeys = Object.keys(byRelationType);
  const result: CalendarPaymentItem[] = [];

  for (const kind of ['dividend', 'coupon'] as const) {
    const relationType = pickPreferredActivePaymentRelationType(relationKeys, kind);
    if (relationType == null) continue;

    const byAccount = byRelationType[relationType];
    if (!byAccount || typeof byAccount !== 'object') continue;

    for (const [accountId, paymentRaw] of Object.entries(byAccount)) {
      if (!isAccountPaymentRow(paymentRaw)) continue;

      result.push(
        buildCalendarPaymentItem({
          dateKey,
          normalizedDate,
          tickerOrIsin,
          relationType,
          accountId,
          payment: paymentRaw,
        })
      );
    }
  }

  return result;
}

function flattenLegacyInstrumentPayments(params: {
  dateKey: string;
  normalizedDate: string;
  tickerOrIsin: string;
  paymentsByKey: Record<string, unknown>;
}): CalendarPaymentItem[] {
  const { dateKey, normalizedDate, tickerOrIsin, paymentsByKey } = params;
  const result: CalendarPaymentItem[] = [];

  for (const [innerKey, paymentRaw] of Object.entries(paymentsByKey)) {
    if (!isAccountPaymentRow(paymentRaw)) continue;

    const payment = paymentRaw as ApiPaymentRow;
    result.push({
      id: payment.id != null ? String(payment.id) : `${dateKey}-${tickerOrIsin}-${innerKey}`,
      isin: payment.isin ?? tickerOrIsin,
      item: payment.item ?? null,
      name_text: payment.name_text ?? '',
      type_id: payment.type_id ?? '',
      ticker: payment.ticker ?? tickerOrIsin,
      sum: payment.sum ?? 0,
      plan_sum: payment.plan_sum ?? null,
      original_plan_sum: payment.original_plan_sum,
      original_sum: payment.original_sum,
      currency_id: payment.currency_id,
      currency: CurrencyConstants.getCurrencySignById(payment.currency_id ?? 1),
      date: payment.date ? moment(payment.date).format('YYYY-MM-DD') : normalizedDate,
      company_name: resolveCompanyNameFromPayment(payment, tickerOrIsin),
      payment_type: payment.payment_type ?? 'dividend',
      raw: paymentRaw as ApiPaymentRow,
    });
  }

  return result;
}

function isNewFormatInstrumentNode(node: Record<string, unknown>): boolean {
  return Object.keys(node).some((key) => isActivePaymentRelationTypeKey(key));
}

/**
 * active/payments → CalendarPaymentItem[].
 * Новый контракт: дата → тикер → relation_type → account_id → строка.
 * На одну бумагу и дату: active.* приоритетнее moex.*.
 */
export const transformPaymentsResponse = (
  apiData: ApiPaymentsData | CalendarPaymentItem[] | null | undefined
): CalendarPaymentItem[] => {
  if (!apiData) return [];
  if (Array.isArray(apiData)) return apiData;

  const result: CalendarPaymentItem[] = [];
  const data = apiData;

  for (const [dateKey, instruments] of Object.entries(data)) {
    if (!instruments) continue;

    const normalizedDate = normalizePaymentsDate(dateKey);

    for (const [tickerOrIsin, instrumentNode] of Object.entries(
      instruments as ApiPaymentsByInstrument
    )) {
      if (!instrumentNode || typeof instrumentNode !== 'object') continue;

      const node = instrumentNode as Record<string, unknown>;

      if (isNewFormatInstrumentNode(node)) {
        result.push(
          ...flattenInstrumentByRelationType({
            dateKey,
            normalizedDate,
            tickerOrIsin,
            byRelationType: node as ApiPaymentsByRelationType,
          })
        );
        continue;
      }

      result.push(
        ...flattenLegacyInstrumentPayments({
          dateKey,
          normalizedDate,
          tickerOrIsin,
          paymentsByKey: node,
        })
      );
    }
  }

  return result;
};
